/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/


import { createRouter, createWebHistory } from 'vue-router'
import doctorRouter from './doctorRouter.js'
import UserSearch from './views/User/Search.vue'
import ErrorPage404 from './views/pages/Error404.vue'
import store from './store/store'
import emitter from './event-bus.js'
import moduleChat from './store/chat/moduleChat.js'
import { analytics, performance } from './firebase.js'
import { logEvent } from 'firebase/analytics'
import { trackScreenView } from './analytics/events.js'
import { trace } from 'firebase/performance'
import { isEstProviderView } from './utils/estDoctorSelection'

let routeTrace = null
const routes = [
  // =============================================================================
  // MAIN LAYOUT ROUTES
  // =============================================================================
  {
    path: '',
    component: () => import('./layouts/main/Main.vue'),
    children: [
      // =============================================================================
      // Theme Routes
      // =============================================================================
      {
        path: '',
        name: 'patient-home',
        component: () => import('./views/User/Home.vue'),
        meta: {
          isHome: true,
          title: 'Home'
        }
      },
      // TB-073: post-login patient dashboard (the landing stays for guests)
      {
        path: '/dashboard',
        name: 'patient-dashboard',
        component: () => import('./views/User/Dashboard.vue'),
        meta: {
          role: 'patient',
          isHome: false,
          title: 'Home'
        }
      },
      {
        path: '/login',
        name: 'login',
        component: () => import('./views/User/Login.vue'),
        meta: {
          isHome: false,
          title: 'Login'
        }
      },
      {
        path: '/signup',
        name: 'signup',
        component: () => import('./views/User/SignUp.vue'),
        meta: {
          isHome: false,
          title: 'Sign Up'
        }
      },
      {
                path: '/help-contact',
                name: 'help-contact',
                component: () =>
                    import ('./views/User/HelpAndContact.vue'),
                meta: {
                    role: 'patient',
                    isHome: false,
                    title: "Help & Contact"
                },
            },
      {
        path: '/verification',
        name: 'verification',
        component: () => import('./views/User/Verification.vue'),
        meta: {
          isHome: false,
          title: 'Verification'
        }
      },
      {
        path: '/signup-information',
        name: 'signup-information',
        component: () => import('./views/User/SignUpInformation.vue'),
        meta: {
          isHome: false,
          title: 'Sign Up Information'
        }
      },
      {
        path: '/edit-profile',
        name: 'edit-profile',
        component: () => import('./views/User/EditProfile.vue'),
        meta: {
          isHome: false,
          title: 'Edit Profile'
        }
      },
      // TB-163: menu entry point for the patient Settings tabs
      {
        path: '/settings',
        name: 'patient-settings',
        redirect: '/profile'
      },
      {
        path: '/profile',
        name: 'profile',
        component: () => import('./views/User/Profile.vue'),
        meta: {
          isHome: false,
          title: 'Profile'
        }
      },
      {
        path: '/forgot-password',
        name: 'forgot-password',
        component: () => import('./views/User/ForgotPassword.vue'),
        meta: {
          isHome: false,
          title: 'Forgot Password'
        }
      },
      {
        path: '/forgot-password-verification',
        name: 'forgot-password-verification',
        component: () => import('./views/User/ForgotPasswordVerification.vue'),
        meta: {
          isHome: false,
          title: 'Forgot Password Verification'
        }
      },
      // TB-140: a patient added by a doctor sets a first password from the
      // welcome e-mail link (#token=). Public: reachable while logged out.
      {
        path: '/set-password',
        name: 'set-password',
        component: () => import('./views/User/SetPassword.vue'),
        meta: {
          isHome: false,
          public: true,
          title: 'Set Password'
        }
      },
      {
        path: '/reset-new-password',
        name: 'reset-new-password',
        component: () => import('./views/User/ResetNewPassword.vue'),
        meta: {
          isHome: false,
          title: 'Reset New Password'
        }
      },
      {
        path: '/change-password',
        name: 'change-password',
        component: () => import('./views/User/ChangePassword.vue'),
        meta: {
          isHome: false,
          title: 'Change Password'
        }
      },
      {
        path: '/search/:searchText',
        name: 'search',
        component: UserSearch,
        meta: {
          isHome: false,
          title: 'Search'
        }
      },
      {
        path: '/dr-profile',
        name: 'dr-profile',
        component: () => import('./views/User/DrProfile.vue'),
        meta: {
          isHome: false,
          title: 'Doctor profile'
        }
      },
        {
        path: '/est-profile',
        name: 'est-profile',
        component: () => import('./views/User/EstProfile.vue'),
        meta: {
          isHome: false,
          title: 'Est Profile Detail'
        }
      },
      {
        path: '/loved-ones',
        name: 'loved-ones',
        component: () => import('./views/User/LovedOnes.vue'),
        meta: {
          isHome: false,
          title: 'Loved Ones'
        }
      },
      // TB-076: the mock payment page is gone; old links land on the real pay step.
      {
        path: '/payment',
        name: 'payment',
        redirect: { name: 'select-card' }
      },
      // TB-076: review and confirmation steps of the one booking flow.
      {
        path: '/booking/review',
        name: 'booking-review',
        component: () => import('./views/User/BookingReview.vue'),
        meta: {
          isHome: false,
          title: 'Review Booking'
        }
      },
      {
        path: '/booking/confirmed',
        name: 'booking-confirmed',
        component: () => import('./views/User/BookingConfirmation.vue'),
        meta: {
          isHome: false,
          title: 'Booking Confirmed'
        }
      },
      {
        path: '/select-card',
        name: 'select-card',
        component: () => import('./views/User/SelectCard.vue'),
        meta: {
          isHome: false,
          title: 'Payment'
        }
      },
       {
        path: '/select-patient',
        name: 'select-patient',
        component: () => import('./views/User/DoctorPatient.vue'),
        meta: {
          isHome: false,
          title: 'Select Patient'
        }
      },
        {
        path: '/add-card',
        name: 'add-card',
        component: () => import('./views/User/AddCard.vue'),
        meta: {
          role: "patient",
          isHome: false,
          title: 'Add Card'
        }
      },
      {
        path: '/appointments',
        name: 'appointments',
        component: () => import('./views/User/Appointments.vue'),
        meta: {
          isHome: false,
          title: 'Appointments'
        }
      },
      {
        path: '/appointment-details/:ID',
        name: 'appointment-details',
        component: () => import('./views/User/AppointmentDetails.vue'),
        meta: {
          isHome: false,
          title: 'Appointment Details'
        }
      },
      {
        path: '/prescriptions',
        name: 'prescriptions',
        component: () => import('./views/User/Prescriptions.vue'),
        meta: {
          isHome: false,
          title: 'Prescriptions'
        }
      },
      {
        // TB-106: every invoice of the patient and their loved ones
        path: '/invoices',
        name: 'invoices',
        component: () => import('./views/User/Invoices.vue'),
        meta: {
          role: 'patient',
          isHome: false,
          title: 'Invoices'
        }
      },
      {
        path: '/invoice/:ID',
        name: 'invoice-view',
        component: () => import('./views/User/InvoiceView.vue'),
        meta: {
          role: 'patient',
          isHome: false,
          title: 'Invoice'
        }
      },
      {
        path: '/notifications',
        name: 'notifications',
        component: () => import('./views/User/Notifications.vue'),
        meta: {
          role: "patient",
          isHome: false,
          title: 'Tabibbi Notifications'
        }
      },
      {
        path: '/reminders',
        name: 'reminders',
        component: () => import('./views/User/RemindersNotifications.vue'),
        meta: {
          role: "patient",
          isHome: false,
          title: 'Reminders & notifications'
        }
      },
      {
        path: '/faqs',
        name: 'FAQs',
        component: () => import('@/views/User/FAQs.vue')
      },
      {
        path: '/about',
        name: 'about',
        component: () => import('@/views/pages/AboutUs.vue')
      },
      {
        path: '/termsandconditions',
        name: 'TermsAndConditions',
        component: () => import('@/views/User/TermsAndConditions.vue')
      },
      {
        path: '/refundandreturnpolicy',
        name: 'RefundAndReturnPolicy',
        component: () => import('@/views/User/RefundAndReturnPolicy.vue')
      },
      {
        path: '/privacy-policy',
        name: 'PrivacyPolicy',
        component: () => import('@/views/User/PrivacyPolicy.vue')
      },
      {
        path: '/contact',
        name: 'contact',
        component: () => import('@/views/pages/ContactUs.vue')
      },
      {
        path: '/chat',
        name: 'chat',
        component: () => import('./views/User/Chat.vue'),
        meta: {
          isHome: false,
          title: 'Chat'
        }
      },
       {
                path: '/call',
                name: 'call',
                component: () =>
                    import ('./views/User/Call.vue'),
                meta: {
                    role: 'patient',
                    isHome: false,
                    title: "Call"
                },
            },
    ]
      
  },
  ...doctorRouter,
  // =============================================================================
  // FULL PAGE LAYOUTS
  // =============================================================================
  {
    path: '',
    component: () => import('@/layouts/full-page/FullPage.vue'),
    children: [
    // =============================================================================
    // PAGES
    // =============================================================================
      {
        path: '/pages/login',
        name: 'page-login',
        component: () => import('@/views/pages/Login.vue')
      },
      {
        path: '/pages/error-404',
        name: 'page-error-404',
        component: ErrorPage404
      },
      // TB-057: public patient consent page, opened from the email (no login)
      {
        path: '/consent/link',
        name: 'consent-link',
        component: () => import('@/views/Consent/LinkConsent.vue'),
        meta: { public: true, title: 'Link request' }
      }
    ]
  },
  // Redirect to 404 page, if no match found
  {
    path: '/:pathMatch(.*)*',
    redirect: '/pages/error-404'
  }
]

const router = createRouter({
  history: createWebHistory(),
  
  routes,
  scrollBehavior (to, from, savedPosition) {
    // if user used back/forward button → restore saved position
    if (savedPosition) {
      return savedPosition
    } else {
      // always scroll to top
      return { top: 0 }
    }
  },
  
})

router.beforeEach((to, from, next) => {
  // TB-071: performance is null when site storage is blocked; never block navigation
  try {
    routeTrace = performance ? trace(performance, `route_${to.name || to.path}`) : null
    if (routeTrace) routeTrace.start()
  } catch (e) {
    routeTrace = null
  }
  next()
})

router.afterEach(() => {
  // TB-080: two overlapping navigations share routeTrace; stopping it twice
  // threw "Trace ... is not running" and rejected the second router.push.
  const t = routeTrace
  routeTrace = null
  if (t) {
    try { t.stop() } catch (e) { /* trace already stopped */ }
  }
})

router.afterEach((to) => {
  if (window.clarity) {
    window.clarity("set", "page", to.name);
  }
});

router.afterEach((to) => {
  if (!analytics) return // TB-071: analytics is null when Firebase could not start
  logEvent(analytics, "page_view", {
    page_title: to.name,
    // to.path, not fullPath: the consent page must never send its #t= token (TB-057)
    page_path: to.path,
  })
})

router.afterEach((to) => {
  if (!analytics) return // TB-071
  trackScreenView({
    screenName: to.name || to.path,
    screenClass: to.meta?.screenClass || "VueScreen"
  })
})

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.classList.add('is-done')
    setTimeout(() => { appLoading.style.display = 'none' }, 380)
  }
})

const protectedUserRoutes = [
  '/appointments', '/chat', '/contact',
  // TB-074: patient inner pages need a session too
  '/notifications', '/reminders', '/prescriptions', '/settings', '/profile', '/edit-profile', '/change-password',
  '/help-contact', '/select-card', '/add-card', '/payment', '/loved-ones', '/select-patient',
  // TB-076
  '/booking/review', '/booking/confirmed'
];
// TB-062: /invoice/:id needs a session too
const protectedUserPrefixes = ['/appointment-details/', '/call', '/invoice/', '/invoices'];

router.beforeEach((to, from, next) => {
  // Set page title
  window.document.title = to.meta && to.meta.title ? `Tabibbi | ${to.meta.title}` : 'Tabibbi';

  // User login check for protected routes
  const isLoggedIn = !!localStorage.getItem('accessToken');
  if ((protectedUserRoutes.includes(to.path) || protectedUserPrefixes.some((p) => to.path.startsWith(p))) && !isLoggedIn) {
    next({ path: '/login', query: { redirect: to.fullPath } });
    return;
  }

  // TB-073: signed-in patients land on their dashboard, not the marketing
  // landing; the dashboard itself needs a patient session.
  try {
    if (to.name === 'patient-home' || to.name === 'patient-dashboard') {
      let u = {}
      try { u = JSON.parse(localStorage.getItem('user')) || {} } catch (e) {}
      const r = u.userRole || u.role
      // 'user' is what sign-up stores for patients before verification ends
      const isPatient = isLoggedIn && (!r || r === 'patient' || r === 'user')
      if (to.name === 'patient-home' && isPatient) {
        next({ name: 'patient-dashboard', replace: true })
        return
      }
      if (to.name === 'patient-dashboard' && !isLoggedIn) {
        next({ path: '/login', query: { redirect: to.fullPath } })
        return
      }
      if (to.name === 'patient-dashboard' && !isPatient) {
        next({ path: r === 'establishment' ? '/doctor/establishment-doctor' : '/doctor' })
        return
      }
    }
  } catch (e) {}

  // Preload chat contacts before entering chat routes
  try {
    if (to?.name === 'chat' || to?.name === 'doctor-chat') {
      try {
        // Guard registration to be idempotent across the app
        const alreadyRegistered = (typeof store.hasModule === 'function' && store.hasModule('chat')) || (!!store._modulesNamespaceMap && !!store._modulesNamespaceMap['chat/']) || !!store._chatModuleRegistered
        if (!alreadyRegistered) {
          try { store.registerModule('chat', moduleChat); store._chatModuleRegistered = true } catch {}
        }
      } catch {}
      // Fire and forget prefetch; component will set up realtime listener
      try { store.dispatch('chat/fetchContacts') } catch {}
    }
  } catch {}
  try {
    // Block navigation to Add Doctor when subscription/role conditions are not met
    if (to?.path === '/doctor/add-doctor') {
      const isSubscribed = localStorage.getItem('isSubscribed')
      let user = null
      try {
        user = JSON.parse(localStorage.getItem('user')) || JSON.parse(localStorage.getItem('userInfo'))
      } catch (e) {}
      const role = user?.userRole || user?.role
      if (isSubscribed === 'false' && role === 'establishment' && !isEstProviderView()) {
        // notify interested components to show subscribe modal and cancel navigation
        try { emitter.emit('showSubscribeModal') } catch (e) {}
        next(false)
        return
      }
    }
  } catch (e) {}

  next();
});

export default router
